import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Popover,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  PushPin as FreezeIcon,
  Search as SearchIcon,
  TableRows as TableIcon,
  ViewAgenda as CardsIcon,
  ViewColumn as ColumnsIcon,
} from "@mui/icons-material";
import { STRONG_DROPDOWN_PAPER_SX } from "../../common/dropdownPaper";
import SearchBar from "../SearchBar";
import ColumnOrderList from "./ColumnOrderList";
import { ColumnId, SheetColumn, SheetSource } from "./sheetTypes";
import { SheetViewState } from "./sheetViewState";

/**
 * Sheet tabs, search, and the columns panel. The header-row switch lives in the
 * columns panel because that is where a person is already looking at column
 * names - and on a workbook whose first row is data, it is the switch that
 * gives that row back.
 */

interface SheetToolbarProps {
  sheetIds: string[];
  sheetNames: string[];
  activeSheetId: string;
  onSheetChange: (sheetId: string) => void;

  query: string;
  onQueryChange: (value: string) => void;
  matchCount: number;
  currentMatch: number;
  matchesTruncated: boolean;
  onPrevMatch: () => void;
  onNextMatch: () => void;

  source: SheetSource;
  state: SheetViewState;
  emptyColumnIds: ColumnId[];
  /** Every column of the sheet in the order it is shown, hidden ones included. */
  orderedColumns: SheetColumn[];
  onToggleColumn: (columnId: ColumnId, visible: boolean) => void;
  onMoveColumn: (columnId: ColumnId, beforeColumnId: ColumnId | null) => void;
  onShowAllColumns: () => void;
  onHideEmptyColumns: () => void;

  headerRowIndex: number | null;
  onHeaderRowToggle: (useFirstRow: boolean) => void;

  frozenCount: number;
  onToggleFreezeFirstColumn: () => void;

  viewMode: "table" | "cards";
  onViewModeChange: (mode: "table" | "cards") => void;

  container?: HTMLElement | null;
  zIndex?: number;
  searchInputRef?: React.Ref<HTMLInputElement>;
}

const SheetToolbar: React.FC<SheetToolbarProps> = ({
  sheetIds,
  sheetNames,
  activeSheetId,
  onSheetChange,
  query,
  onQueryChange,
  matchCount,
  currentMatch,
  matchesTruncated,
  onPrevMatch,
  onNextMatch,
  source,
  state,
  emptyColumnIds,
  orderedColumns,
  onToggleColumn,
  onMoveColumn,
  onShowAllColumns,
  onHideEmptyColumns,
  headerRowIndex,
  onHeaderRowToggle,
  frozenCount,
  onToggleFreezeFirstColumn,
  viewMode,
  onViewModeChange,
  container,
  zIndex,
  searchInputRef,
}) => {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const [columnsAnchor, setColumnsAnchor] = useState<HTMLElement | null>(null);
  const [columnSearch, setColumnSearch] = useState("");

  const hiddenColumns = useMemo(() => {
    const set: Record<string, true> = {};
    state.hiddenColumns.forEach((id) => {
      set[id] = true;
    });
    return set;
  }, [state.hiddenColumns]);

  const visibleCount = source.columns.length - state.hiddenColumns.length;

  // In the order the person is looking at, not the file's: a checklist that
  // disagrees with the header row is a checklist nobody can find a column in.
  const listedColumns = useMemo<SheetColumn[]>(() => {
    const needle = columnSearch.trim().toLowerCase();
    if (!needle) return orderedColumns;
    return orderedColumns.filter(
      (column) =>
        column.letter.toLowerCase().indexOf(needle) !== -1 ||
        column.label.toLowerCase().indexOf(needle) !== -1,
    );
  }, [orderedColumns, columnSearch]);

  const reorderEnabled = columnSearch.trim().length === 0;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 2,
        py: 1,
        borderBottom: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
        flexShrink: 0,
        flexWrap: "wrap",
      }}
    >
      {sheetIds.length > 1 &&
        (isNarrow ? (
          <Select
            size="small"
            value={activeSheetId}
            onChange={(event) => onSheetChange(event.target.value)}
            sx={{ flex: "1 1 140px", fontSize: 13 }}
            MenuProps={{
              container: container ?? undefined,
              sx: zIndex != null ? { zIndex: zIndex + 1 } : undefined,
            }}
          >
            {sheetIds.map((id, index) => (
              <MenuItem key={id} value={id}>
                {sheetNames[index] || `Sheet ${index + 1}`}
              </MenuItem>
            ))}
          </Select>
        ) : (
          <Tabs
            value={activeSheetId}
            onChange={(_, value) => onSheetChange(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              flex: 1,
              minHeight: 36,
              "& .MuiTab-root": {
                minHeight: 36,
                py: 0.5,
                textTransform: "none",
              },
            }}
          >
            {sheetIds.map((id, index) => (
              <Tab
                key={id}
                value={id}
                label={sheetNames[index] || `Sheet ${index + 1}`}
              />
            ))}
          </Tabs>
        ))}

      <Box sx={{ flex: sheetIds.length > 1 ? "0 0 auto" : 1 }} />

      <ToggleButtonGroup
        size="small"
        exclusive
        value={viewMode}
        onChange={(_, value) => {
          if (value === "table" || value === "cards") onViewModeChange(value);
        }}
        sx={{ flexShrink: 0 }}
      >
        <ToggleButton value="table" aria-label="Table layout" sx={{ px: 1 }}>
          <Tooltip title="Table">
            <TableIcon fontSize="small" />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="cards" aria-label="Card layout" sx={{ px: 1 }}>
          <Tooltip title="Cards - one row at a time, field by field">
            <CardsIcon fontSize="small" />
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>

      <Tooltip
        title={
          frozenCount > 0
            ? `${frozenCount} column${frozenCount === 1 ? "" : "s"} frozen - click to unfreeze`
            : "Keep the first column in place while the rest scrolls"
        }
      >
        <Button
          size="small"
          startIcon={<FreezeIcon />}
          onClick={onToggleFreezeFirstColumn}
          color={frozenCount > 0 ? "primary" : "inherit"}
          sx={{ textTransform: "none", flexShrink: 0 }}
        >
          {frozenCount > 0 ? `Frozen ${frozenCount}` : "Freeze"}
        </Button>
      </Tooltip>

      <Tooltip title="Show or hide columns">
        <Button
          size="small"
          startIcon={<ColumnsIcon />}
          onClick={(event) => setColumnsAnchor(event.currentTarget)}
          sx={{ textTransform: "none", flexShrink: 0 }}
        >
          Columns {visibleCount}/{source.columns.length}
        </Button>
      </Tooltip>

      <SearchBar
        inputRef={searchInputRef}
        value={query}
        onChange={onQueryChange}
        matchCount={matchCount}
        currentMatch={matchCount > 0 ? currentMatch + 1 : 0}
        onPrev={onPrevMatch}
        onNext={onNextMatch}
      />
      {matchesTruncated && (
        <Typography variant="caption" color="text.secondary">
          first {matchCount.toLocaleString()} shown
        </Typography>
      )}

      <Popover
        open={!!columnsAnchor}
        anchorEl={columnsAnchor}
        onClose={() => setColumnsAnchor(null)}
        container={container ?? undefined}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: [
              STRONG_DROPDOWN_PAPER_SX,
              { width: { xs: "calc(100vw - 32px)", sm: 320 } },
            ],
          },
        }}
        sx={zIndex != null ? { zIndex } : undefined}
      >
        <Box sx={{ p: 1.5, pb: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Columns {visibleCount} of {source.columns.length}
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={columnSearch}
            onChange={(event) => setColumnSearch(event.target.value)}
            placeholder="Search columns"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                size="small"
                checked={headerRowIndex != null}
                onChange={(event) => onHeaderRowToggle(event.target.checked)}
              />
            }
            label={
              <Typography variant="body2">Use row 1 as headers</Typography>
            }
          />
        </Box>
        <Divider />
        <ColumnOrderList
          columns={listedColumns}
          hiddenColumns={hiddenColumns}
          reorderEnabled={reorderEnabled}
          onToggleColumn={onToggleColumn}
          onMoveColumn={onMoveColumn}
        />
        {!reorderEnabled && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", px: 1.5, pb: 0.5 }}
          >
            Clear the search to reorder columns.
          </Typography>
        )}
        <Divider />
        <Box sx={{ display: "flex", gap: 1, p: 1 }}>
          <Button
            size="small"
            onClick={onShowAllColumns}
            disabled={state.hiddenColumns.length === 0}
            sx={{ textTransform: "none" }}
          >
            Show all
          </Button>
          <Button
            size="small"
            onClick={onHideEmptyColumns}
            disabled={emptyColumnIds.length === 0}
            sx={{ textTransform: "none" }}
          >
            Hide empty ({emptyColumnIds.length})
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};

export default SheetToolbar;
